import type { ErrorEvent, Event } from '@sentry/vue'
import * as Sentry from '@sentry/vue'

export type TRegistrationClearedSource = 'http' | 'websocket' | 'pooling'

export type TRegistrationClearedReason = 'server_revoke' | 'ambiguous_streak'

const REGISTRATION_CLEARED_MESSAGE = 'Player registration cleared (persisted credentials removed)'

/**
 * Intentional tenant unregister / operator delete — keep quiet.
 * Unexpected auth revokes (`invalid_secret`, `player_not_found_in_tenant`, …) must report so
 * post-claim orphan drops are visible in Sentry (#1486).
 */
export const EXPECTED_SILENT_REGISTRATION_CLEAR_CODES = new Set<string>([
  'tenant_unregister'
])

const MAX_TAG_LEN = 120

function truncateTag(value: string): string {
  return value.length <= MAX_TAG_LEN ? value : `${value.slice(0, MAX_TAG_LEN - 1)}…`
}

export function isSilentExpectedRegistrationClear(params: {
  reason: TRegistrationClearedReason
  code?: string
}): boolean {
  return params.reason === 'server_revoke'
    && params.code != null
    && EXPECTED_SILENT_REGISTRATION_CLEAR_CODES.has(params.code)
}

/** Drop intentional unregister telemetry (including older player builds that only tagged reason). */
export function isExpectedRegistrationClearSentryEvent(event: Event | ErrorEvent): boolean {
  const message = event.message ?? event.exception?.values?.[0]?.value ?? ''
  if (message !== REGISTRATION_CLEARED_MESSAGE) {
    return false
  }
  if (event.tags?.registration_clear_reason !== 'server_revoke') {
    return false
  }
  const code = event.tags?.registration_clear_code
  return typeof code === 'string' && EXPECTED_SILENT_REGISTRATION_CLEAR_CODES.has(code)
}

/**
 * Reports persisted registration clears to Sentry.
 * Intentional server unregisters (`code: tenant_unregister`) stay silent; unexpected revokes and
 * ambiguous-streak clears are warnings (fingerprinted by reason/code/source).
 */
export function reportRegistrationClearedToSentry(params: {
  reason: TRegistrationClearedReason
  source: TRegistrationClearedSource
  /** e.g. `fetch_registration_bootstrap` from usePlayer */
  context?: string
  code?: string
  httpStatus?: number
}): void {
  const { reason, source, context, code, httpStatus } = params
  if (isSilentExpectedRegistrationClear({ reason, code })) {
    return
  }

  const status = httpStatus ?? 401

  Sentry.addBreadcrumb({
    category: 'player.registration',
    message: 'registration_cleared',
    level: 'warning',
    data: { context, code, status, reason, source }
  })

  Sentry.captureMessage(REGISTRATION_CLEARED_MESSAGE, {
    level: 'warning',
    tags: {
      player_registration_cleared: 'true',
      registration_clear_reason: reason,
      registration_clear_source: source,
      ...(context != null ? { registration_clear_context: truncateTag(context) } : {}),
      ...(code != null ? { registration_clear_code: truncateTag(code) } : {})
    },
    fingerprint: ['player-registration-cleared', reason, code ?? 'no_code', source],
    extra: {
      context,
      code,
      httpStatus: status
    }
  })
}
